/**
 * Device screenshot URL resolver.
 *
 * Per-environment, the Videri Canvas Service publishes the most recent live
 * screenshot of each canvas to a public CDN, keyed by the device's hardware
 * ID. No auth required — the browser can load these directly via <img>.
 *
 * URL pattern:
 *   https://cdn.{envSubdomain}.videri.com/videri-{envSlug}-canvas-service/
 *     screenshots/{deviceId}.jpg?cachebust={uuid}
 *
 * Freshness: each request is served from the CDN edge. To force a refresh
 * (e.g. "show live playback"), pass a new cacheBuster value on a timer.
 * Without it the browser caches the image — expected behavior for offline
 * devices where the snapshot isn't changing anyway.
 */

const ENV_SCREENSHOT_CDN = {
  dev: "https://cdn.dev.videri.com/videri-development-canvas-service/screenshots",
  qa: "https://cdn.qa.videri.com/videri-qa-canvas-service/screenshots",
  prod: "https://cdn.go.videri.com/videri-production-canvas-service/screenshots",
};

/**
 * Build the CDN URL for a device's live screenshot.
 *
 * @param {string} envId         "dev" | "qa" | "prod" (from cmsAuth.environment)
 * @param {string} deviceId      Device hardware ID (the code printed on the device,
 *                               e.g. "05K1Q3260095" — this is the `deviceId`
 *                               field returned by getDevices(), NOT the numeric
 *                               canvas id).
 * @param {number|string} [cacheBuster] Optional value appended as ?cachebust=
 *                                      Changing this forces the browser to
 *                                      re-fetch. Omit for a cached image.
 * @returns {string|null}        Full URL, or null if deviceId missing.
 */
export function getDeviceScreenshotUrl(envId, deviceId, cacheBuster) {
  if (!deviceId) return null;
  const base = ENV_SCREENSHOT_CDN[envId] || ENV_SCREENSHOT_CDN.prod;
  const cb = cacheBuster != null ? `?cachebust=${encodeURIComponent(cacheBuster)}` : "";
  return `${base}/${deviceId}.jpg${cb}`;
}
